import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from './AdminAuthContext';
import './css/VisitorLogs.css';

const rangeOptions = [
  { value: 1, label: '24 hours' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const VisitorLogs = () => {
  const { adminToken } = useAdminAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [rangeDays, setRangeDays] = useState(30);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [botFilter, setBotFilter] = useState('exclude');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${adminToken}` }), [adminToken]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [logsResponse, statsResponse] = await Promise.all([
        axios.get('/api/admin/visitor-logs', {
          headers,
          params: { page, limit: 50, search: search.trim() || undefined, bots: botFilter },
        }),
        axios.get('/api/admin/visitor-stats', { headers, params: { days: rangeDays } }),
      ]);
      setLogs(logsResponse.data?.logs || []);
      setPagination(logsResponse.data?.pagination || { page: 1, pages: 1, total: 0 });
      setStats(statsResponse.data || null);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load visitor analytics.');
    } finally {
      setLoading(false);
    }
  }, [botFilter, headers, page, rangeDays, search]);

  useEffect(() => {
    const timer = window.setTimeout(loadData, 250);
    return () => window.clearTimeout(timer);
  }, [loadData]);

  const maxDailyViews = Math.max(...(stats?.daily || []).map((entry) => Number(entry.views)), 1);
  const summary = stats?.summary || {};

  return (
    <main className="visitor-analytics">
      <header className="visitor-analytics-head">
        <div>
          <p className="visitor-kicker">Private administrator analytics</p>
          <h1>Visitor Activity</h1>
          <p>Page views are retained for 90 days. Browsers with Do Not Track enabled are not recorded.</p>
        </div>
        <div className="visitor-actions">
          <label htmlFor="visitor-range">Reporting range</label>
          <select id="visitor-range" value={rangeDays} onChange={(event) => setRangeDays(Number(event.target.value))}>
            {rangeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <button type="button" onClick={loadData} disabled={loading}>Refresh</button>
        </div>
      </header>

      {error && <p className="visitor-error" role="alert">{error}</p>}

      <section className="visitor-summary" aria-label="Visitor summary">
        <article><span>Total views</span><strong>{Number(summary.totalViews || 0).toLocaleString()}</strong></article>
        <article><span>Unique visitors</span><strong>{Number(summary.uniqueVisitors || 0).toLocaleString()}</strong></article>
        <article><span>Views today</span><strong>{Number(summary.todayViews || 0).toLocaleString()}</strong></article>
        <article><span>Bot requests</span><strong>{Number(summary.botViews || 0).toLocaleString()}</strong></article>
      </section>

      <section className="visitor-insights">
        <div className="visitor-chart-panel">
          <div className="visitor-section-title">
            <h2>Daily traffic</h2>
            <span>Human visits</span>
          </div>
          <div className="visitor-bars" aria-label="Daily visitor bar chart">
            {(stats?.daily || []).length === 0 && <p className="visitor-empty">Traffic will appear after new visits are recorded.</p>}
            {(stats?.daily || []).map((entry) => (
              <div className="visitor-bar-column" key={entry.date} title={`${entry.date}: ${entry.views} views, ${entry.visitors} visitors`}>
                <span>{entry.views}</span>
                <div className="visitor-bar" style={{ height: `${Math.max((Number(entry.views) / maxDailyViews) * 100, 4)}%` }} />
                <time dateTime={entry.date}>{new Date(`${entry.date}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</time>
              </div>
            ))}
          </div>
        </div>

        <div className="visitor-top-pages">
          <div className="visitor-section-title">
            <h2>Top pages</h2>
            <span>Selected range</span>
          </div>
          <ol>
            {(stats?.topPages || []).map((entry) => (
              <li key={entry.page}>
                <span title={entry.page}>{entry.page}</span>
                <strong>{Number(entry.views).toLocaleString()}</strong>
              </li>
            ))}
          </ol>
          {(stats?.topPages || []).length === 0 && <p className="visitor-empty">No page data in this range.</p>}
        </div>
      </section>

      <section className="visitor-log-section">
        <div className="visitor-log-toolbar">
          <div>
            <h2>Activity log</h2>
            <span>{pagination.total.toLocaleString()} recorded events</span>
          </div>
          <div className="visitor-filters">
            <input
              type="search"
              value={search}
              placeholder="Filter by page"
              aria-label="Filter visitor logs by page"
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            />
            <select value={botFilter} aria-label="Bot filter" onChange={(event) => { setBotFilter(event.target.value); setPage(1); }}>
              <option value="exclude">People only</option>
              <option value="all">People and bots</option>
              <option value="only">Bots only</option>
            </select>
          </div>
        </div>

        <div className="visitor-table-wrap">
          <table>
            <thead><tr><th>Time</th><th>Page</th><th>IP</th><th>Browser / device</th><th>Type</th></tr></thead>
            <tbody>
              {!loading && logs.length === 0 && <tr><td colSpan="5" className="visitor-empty">No matching visits.</td></tr>}
              {logs.map((log) => (
                <tr key={log.id}>
                  <td data-label="Time"><time dateTime={log.timestamp}>{new Date(log.timestamp).toLocaleString('en-GB')}</time></td>
                  <td data-label="Page">{log.page}</td>
                  <td data-label="IP">{log.ip}</td>
                  <td data-label="Browser / device" className="visitor-user-agent" title={log.user_agent || ''}>{log.user_agent || 'Unknown'}</td>
                  <td data-label="Type"><span className={log.is_bot ? 'visitor-type bot' : 'visitor-type'}>{log.is_bot ? 'Bot' : 'Person'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="visitor-pagination">
          <button type="button" disabled={pagination.page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>Previous</button>
          <span>Page {pagination.page} of {pagination.pages}</span>
          <button type="button" disabled={pagination.page >= pagination.pages || loading} onClick={() => setPage((current) => current + 1)}>Next</button>
        </div>
      </section>
    </main>
  );
};

export default VisitorLogs;
